import { REALTIME_PATH } from '@an-crm/shared';
import type { Socket } from 'socket.io-client';
import { sessionStore } from '@/lib/auth/session-store';

let warned = false;

function warnOnce(message: string, detail?: unknown): void {
  if (warned) return;
  warned = true;
  console.warn(`[realtime] ${message} — live updates are off; the app keeps working.`, detail ?? '');
}

/**
 * Opens the Socket.IO connection (the client library is loaded lazily) and keeps it in step with the
 * in-memory access token: a new token reconnects, no token disconnects. Never throws and never
 * blocks rendering — if the socket cannot connect the app simply falls back to fetching.
 */
export function startRealtime(bind: (socket: Socket) => void): () => void {
  let socket: Socket | null = null;
  let stopped = false;
  let lastToken = sessionStore.getAccessToken();

  import('socket.io-client')
    .then(({ io }) => {
      if (stopped) return;
      const s = io({
        path: REALTIME_PATH,
        auth: (cb) => cb({ token: sessionStore.getAccessToken() }),
        transports: ['websocket'],
        reconnectionDelayMax: 30_000,
        autoConnect: Boolean(sessionStore.getAccessToken()),
      });
      s.on('connect_error', (err: Error) => warnOnce('could not connect', err.message));
      bind(s);
      socket = s;
    })
    .catch((err: unknown) => warnOnce('client failed to load', err));

  const unsubscribe = sessionStore.subscribe(() => {
    const token = sessionStore.getAccessToken();
    if (token === lastToken) return;
    lastToken = token;
    if (!socket) return;
    socket.disconnect();
    if (token) socket.connect();
  });

  return () => {
    stopped = true;
    unsubscribe();
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
  };
}
